/**
 * Pure annual/period report payload — shared by PDF renderer and Section 1 tests.
 * Numbers come only from calculations.ts (same as on-screen hooks).
 */

import {
  applyFilters,
  compute503020,
  computeDashboardSummary,
  computeDebtPayoff,
  computeMonthCategorySummary,
  computeMonthlyBreakdown,
  computeNetWorthStats,
  computeSinkingFundStats,
  formatCurrencyAmount,
  type DashboardSummary,
  type Rule503020Result,
} from "../calculations";
import type {
  Account,
  Category,
  Debt,
  NetWorthLog,
  SinkingFund,
  Transaction,
} from "../schema";

export interface ReportMoneyFmt {
  currency: string;
  locale: string;
}

export interface ReportKpiRow {
  key: string;
  label: string;
  /** Raw numeric for tests */
  value: number;
  /** Display string (locale-aware) */
  display: string;
}

export interface ReportCategoryRow {
  id: string;
  name: string;
  group: string;
  target: number;
  actual: number;
  variance: number;
}

export interface ReportDebtSummaryRow {
  id: string;
  name: string;
  balance: number;
  interestRate: number;
  totalInterest: number;
  monthsToPayoff: number;
  payoffDateIso: string;
  order: number;
}

export interface ReportFundRow {
  id: string;
  name: string;
  targetAmount: number;
  currentSaved: number;
  pct: number;
  remaining: number;
  monthsLeft: number;
  monthlyNeeded: number;
}

export interface ReportAccountRow {
  id: string;
  name: string;
  type: string;
  balance: number;
}

export interface AnnualReportPayload {
  brand: string;
  title: string;
  periodLabel: string;
  year: number;
  month: number | null;
  generatedAtIso: string;
  person: string;
  currency: string;
  locale: string;
  dashboard: DashboardSummary;
  kpis: ReportKpiRow[];
  monthlyCashFlow: {
    month: number;
    income: number;
    expense: number;
    leftover: number;
  }[];
  budget: {
    month: number;
    income: ReportCategoryRow[];
    expense: ReportCategoryRow[];
    groups: { group: string; target: number; actual: number }[];
  };
  rule503020: Rule503020Result;
  debts: {
    totalBalance: number;
    totalInterest: number;
    debtFreeDateIso: string | null;
    rows: ReportDebtSummaryRow[];
  };
  sinkingFunds: ReportFundRow[];
  netWorth: {
    stats: ReturnType<typeof computeNetWorthStats>;
    logs: { monthYear: string; assets: number; debts: number; net: number }[];
    accounts: ReportAccountRow[];
  };
  /** Flattened assertion map: key → raw number appearing in the report */
  assertionNumbers: Record<string, number>;
}

function money(n: number, fmt: ReportMoneyFmt, compact = false): string {
  return formatCurrencyAmount(n, fmt.currency, fmt.locale, compact);
}

export interface BuildAnnualReportInput {
  year: number;
  /**
   * Explicit month for budget + 50/30/20.
   * When omitted, resolves to the latest month in `year` that has transactions
   * (same idea as “last month with data” for an annual report).
   */
  month?: number;
  person?: string;
  transactions: Transaction[];
  categories: Category[];
  debts: Debt[];
  sinkingFunds: SinkingFund[];
  netWorthLogs: NetWorthLog[];
  accounts?: Account[];
  currency?: string;
  locale?: string;
  generatedAt?: Date;
  debtMethod?: "snowball" | "avalanche";
}

/**
 * Single source of truth for which month budget / 50/30/20 sections use.
 * Prefer an explicit month; otherwise the latest month with activity in the year.
 */
export function resolveReportMonth(
  year: number,
  transactions: Transaction[],
  explicitMonth?: number,
  asOf: Date = new Date()
): number {
  if (explicitMonth != null && explicitMonth >= 1 && explicitMonth <= 12) {
    return explicitMonth;
  }
  let latest = 0;
  const prefix = `${year}-`;
  for (const t of transactions) {
    if (t.skipped) continue;
    if (!t.date.startsWith(prefix)) continue;
    const m = parseInt(t.date.slice(5, 7), 10);
    if (!isNaN(m) && m > latest) latest = m;
  }
  if (latest > 0) return latest;
  if (asOf.getFullYear() === year) return asOf.getMonth() + 1;
  return 12;
}

export function buildAnnualReportPayload(input: BuildAnnualReportInput): AnnualReportPayload {
  const year = input.year;
  const person = input.person ?? "All";
  const generatedAt = input.generatedAt ?? new Date();
  const yearTxs = applyFilters(input.transactions, { year, person });
  const month = resolveReportMonth(year, yearTxs, input.month, generatedAt);
  const fmt: ReportMoneyFmt = {
    currency: input.currency ?? "USD",
    locale: input.locale ?? "en-US",
  };
  const method = input.debtMethod ?? "snowball";

  const dashboard = computeDashboardSummary(yearTxs, input.categories);
  const monthly = computeMonthlyBreakdown(yearTxs, year);
  const budgetSummary = computeMonthCategorySummary(
    yearTxs,
    input.categories,
    year,
    month
  );
  const rule = compute503020(yearTxs, input.categories, year, month);
  const debtResults = computeDebtPayoff(input.debts, method, generatedAt);

  const monthLabel = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ][month - 1];

  // Labels are translated at render via pdf.kpi.*; display uses full precision (matches table cells).
  const kpis: ReportKpiRow[] = [
    { key: "moneyIn", label: "Money In", value: dashboard.income, display: money(dashboard.income, fmt, false) },
    { key: "moneyOut", label: "Money Out", value: dashboard.expense, display: money(dashboard.expense, fmt, false) },
    { key: "leftover", label: "Leftover", value: dashboard.leftover, display: money(dashboard.leftover, fmt, false) },
    {
      key: "savingsRate",
      label: "Savings Rate",
      value: dashboard.savingsRate,
      display: `${dashboard.savingsRate.toFixed(1)}%`,
    },
    {
      key: "totalSaved",
      label: "Total Saved",
      value: dashboard.actualSaved,
      display: money(dashboard.actualSaved, fmt, false),
    },
  ];

  const incomeRows: ReportCategoryRow[] = budgetSummary.income.map((c) => ({
    id: c.id,
    name: c.name,
    group: c.group,
    target: c.monthlyTarget,
    actual: c.actual,
    variance: c.variance,
  }));
  const expenseRows: ReportCategoryRow[] = budgetSummary.expense.map((c) => ({
    id: c.id,
    name: c.name,
    group: c.group,
    target: c.monthlyTarget,
    actual: c.actual,
    variance: c.variance,
  }));

  const groups = (["needs", "wants", "savings"] as const).map((g) => {
    const cats = expenseRows.filter((c) => c.group === g);
    return {
      group: g,
      target: cats.reduce((s, c) => s + c.target, 0),
      actual: cats.reduce((s, c) => s + c.actual, 0),
    };
  });

  const debtRows: ReportDebtSummaryRow[] = debtResults.map((r, i) => ({
    id: r.debt.id,
    name: r.debt.name,
    balance: r.debt.balance,
    interestRate: r.debt.interestRate,
    totalInterest: r.totalInterest,
    monthsToPayoff: r.monthsToPayoff,
    payoffDateIso: r.payoffDate.toISOString().slice(0, 10),
    order: i + 1,
  }));

  const totalDebtBalance = input.debts.reduce((s, d) => s + d.balance, 0);
  const totalDebtInterest = debtRows.reduce((s, r) => s + r.totalInterest, 0);
  const debtFree =
    debtRows.length > 0 ? debtRows[debtRows.length - 1].payoffDateIso : null;

  const sinkingFunds: ReportFundRow[] = input.sinkingFunds.map((f) => {
    const stats = computeSinkingFundStats(f, generatedAt);
    return {
      id: f.id,
      name: f.name,
      targetAmount: f.targetAmount,
      currentSaved: f.currentSaved,
      pct: stats.pct,
      remaining: stats.remaining,
      monthsLeft: stats.monthsLeft,
      monthlyNeeded: stats.monthlyNeeded,
    };
  });

  const nwStats = computeNetWorthStats(input.netWorthLogs);
  const nwLogs = [...input.netWorthLogs]
    .sort((a, b) => a.monthYear.localeCompare(b.monthYear))
    .map((l) => ({
      monthYear: l.monthYear,
      assets: l.assetsTotal,
      debts: l.debtsTotal,
      net: l.assetsTotal - l.debtsTotal,
    }));

  const accounts: ReportAccountRow[] = (input.accounts ?? []).map((a) => ({
    id: a.id,
    name: a.name,
    type: a.type,
    balance: a.initialBalance,
  }));

  const assertionNumbers: Record<string, number> = {
    "dashboard.income": dashboard.income,
    "dashboard.expense": dashboard.expense,
    "dashboard.leftover": dashboard.leftover,
    "dashboard.savingsRate": dashboard.savingsRate,
    "dashboard.actualSaved": dashboard.actualSaved,
    "dashboard.totalSavedTarget": dashboard.totalSavedTarget,
    "rule.income": rule.income,
    "rule.needs": rule.needs,
    "rule.wants": rule.wants,
    "rule.savings": rule.savings,
    "rule.needsPct": rule.needsPct,
    "rule.wantsPct": rule.wantsPct,
    "rule.savingsPct": rule.savingsPct,
    "debts.totalBalance": totalDebtBalance,
    "debts.totalInterest": totalDebtInterest,
    "netWorth.net": nwStats.netWorth,
    "netWorth.assets": nwStats.assets,
    "netWorth.debts": nwStats.debts,
  };

  monthly.forEach((m) => {
    assertionNumbers[`cashflow.${m.month}.income`] = m.totalIncome;
    assertionNumbers[`cashflow.${m.month}.expense`] = m.totalExpense;
    assertionNumbers[`cashflow.${m.month}.leftover`] = m.leftover;
  });

  incomeRows.forEach((r) => {
    assertionNumbers[`budget.income.${r.id}.actual`] = r.actual;
    assertionNumbers[`budget.income.${r.id}.target`] = r.target;
  });
  expenseRows.forEach((r) => {
    assertionNumbers[`budget.expense.${r.id}.actual`] = r.actual;
    assertionNumbers[`budget.expense.${r.id}.target`] = r.target;
  });
  debtRows.forEach((r) => {
    assertionNumbers[`debt.${r.id}.balance`] = r.balance;
    assertionNumbers[`debt.${r.id}.totalInterest`] = r.totalInterest;
  });
  sinkingFunds.forEach((f) => {
    assertionNumbers[`fund.${f.id}.current`] = f.currentSaved;
    assertionNumbers[`fund.${f.id}.target`] = f.targetAmount;
    assertionNumbers[`fund.${f.id}.pct`] = f.pct;
  });

  return {
    brand: "BudgetOS",
    title: `${year} Annual Report`,
    periodLabel: `${year} Annual Report · detail ${monthLabel}`,
    year,
    month,
    generatedAtIso: generatedAt.toISOString(),
    person,
    currency: fmt.currency,
    locale: fmt.locale,
    dashboard,
    kpis,
    monthlyCashFlow: monthly.map((m) => ({
      month: m.month,
      income: m.totalIncome,
      expense: m.totalExpense,
      leftover: m.leftover,
    })),
    budget: {
      month,
      income: incomeRows,
      expense: expenseRows,
      groups,
    },
    rule503020: rule,
    debts: {
      totalBalance: totalDebtBalance,
      totalInterest: totalDebtInterest,
      debtFreeDateIso: debtFree,
      rows: debtRows,
    },
    sinkingFunds,
    netWorth: {
      stats: nwStats,
      logs: nwLogs,
      accounts,
    },
    assertionNumbers: {
      ...assertionNumbers,
      "report.month": month,
    },
  };
}

/** Monthly budget section payload (reuses annual builder subset). */
export function buildMonthlyBudgetPayload(
  input: BuildAnnualReportInput & { month: number }
): Pick<AnnualReportPayload, "budget" | "year" | "month" | "currency" | "locale" | "assertionNumbers" | "brand" | "title" | "periodLabel" | "generatedAtIso"> {
  const full = buildAnnualReportPayload(input);
  return {
    brand: full.brand,
    title: `Monthly Budget — ${full.year}-${String(input.month).padStart(2, "0")}`,
    periodLabel: full.title,
    year: full.year,
    month: input.month,
    generatedAtIso: full.generatedAtIso,
    currency: full.currency,
    locale: full.locale,
    budget: full.budget,
    assertionNumbers: Object.fromEntries(
      Object.entries(full.assertionNumbers).filter(
        ([k]) => k.startsWith("budget.") || k.startsWith("rule.")
      )
    ),
  };
}
